import React, { useRef } from "react";
import styles from "./FileInput.module.scss";
import { Controller } from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import toast from "@/lib/toast";
import ViewFileFormat from "./components/ViewFileFormat/ViewFileFormat";

const FileInput = (props) => {
  const {
    control,
    register,
    validations,
    name,
    placeholder = "Select files...",
    ...rest
  } = props;

  const inputRef = useRef(null);

  const renderInput = (field) => {
    const handleFileChange = async (e) => {
      const files = Array.from(e.target.files);
      const accept = e.target.accept
        ?.split(",")
        .map((a) => a.trim().toLowerCase());

      const invalidFiles = [];

      const processFile = (file) => {
        console.log("file.size", file.size / 1024 / 1024, "MB");
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = () => {
            const img = new Image();
            img.src = reader.result;

            img.onload = () => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              canvas.width = 1000;
              canvas.height = 1000;
              ctx.drawImage(img, 0, 0, 1000, 1000);

              let quality = 0.95;

              const compress = () => {
                canvas.toBlob(
                  (blob) => {
                    console.log("blob.size", blob.size / 1024 / 1024, "MB");
                    if (blob.size <= 2 * 1024 * 1024 || quality < 0.1) {
                      const finalFile = new File([blob], file.name, {
                        type: blob.type,
                        lastModified: Date.now(),
                      });
                      resolve(finalFile);
                    } else {
                      quality -= 0.05;
                      compress();
                    }
                  },
                  "image/jpeg",
                  quality
                );
              };
              compress();
            };
          };
        });
      };

      const validFilePromises = files.map((file) => {
        const fileExt = "." + file.name.split(".").pop().toLowerCase();
        const mimeType = file.type.toLowerCase();

        const isValid = accept?.some((rule) => {
          if (rule.startsWith(".")) return fileExt === rule;
          else if (rule.endsWith("/*"))
            return mimeType.startsWith(rule.split("/")[0] + "/");
          else return mimeType === rule;
        });

        if (isValid) {
          return processFile(file);
        } else {
          invalidFiles.push(file.name);
          return null;
        }
      });

      const finalFiles = await Promise.all(validFilePromises);
      const filteredFiles = finalFiles.filter(Boolean);

      if (invalidFiles.length > 0) {
        toast.error(`These files are not allowed: ${invalidFiles.join(", ")}`);
      }

      field.onChange(filteredFiles);
    };

    const handleRemoveFile = (index) => {
      const newFiles = field.value.filter((_, i) => i !== index);
      field.onChange(newFiles);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    return (
      <div className={styles.fileWrapper}>
        <input
          {...rest}
          type="file"
          id={name}
          name={name}
          ref={inputRef}
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
        />

        <div className={`${styles.inputField} ${styles.chipInput}`}>
          {field.value && field.value.length > 0 ? (
            field.value.map((file, index) => {
              const isImage =
                (typeof file === "string" &&
                  /\.(jpeg|jpg|png|gif|webp)$/.test(file)) ||
                (typeof file !== "string" && file.type?.startsWith("image/"));

              const fileName =
                typeof file === "string" ? file.split("/").pop() : file.name;

              const imageUrl =
                typeof file === "string"
                  ? file // backend URL
                  : URL.createObjectURL(file); // local file preview

              return (
                <abbr key={index} title={fileName}>
                  <span className={styles.fileChip}>
                    {isImage ? (
                      <img
                        src={imageUrl}
                        alt={fileName}
                        className={styles.previewImage}
                      />
                    ) : (
                      <p>{fileName}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className={styles.removeChip}
                    >
                      &times;
                    </button>
                  </span>
                </abbr>
              );
            })
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <label htmlFor={name} className={styles.fileButton}>
          Select Files
        </label>
      </div>
    );
  };

  if (!register) {
    return (
      <Controller
        control={control}
        rules={validations}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    const field = register(name, {
      ...validations,
      onChange: (e) => validations?.onChange?.(e) || Array.from(e.target.files),
    });
    return renderInput(field);
  }
};

const FileInputWithLabelAndError = withLabelAndError(FileInput, "View File Format", ViewFileFormat);

export default FileInputWithLabelAndError;
export { FileInput };
